/**
 * Lockstep Platform SDK for TypeScript
 *
 * (c) 2021-2023 Lockstep, Inc.
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 *
 * @author     Lockstep Network <support@lockstep.io>
 * @copyright  2021-2023 Lockstep, Inc.
 * @link       https://github.com/Lockstep-Network/lockstep-sdk-typescript
 */
export class WebhookRulesClient {
    /**
     * Internal constructor for this client library
     */
    constructor(client) {
        this.client = client;
    }
    /**
     * Retrieves the Webhook Rule specified by this unique identifier.
     *
     * @param id The unique Lockstep Platform ID number of this Webhook Rule
     */
    retrieveWebhookRule(id) {
        const url = `/api/v1/WebhookRules/${id}`;
        return this.client.request("get", url, null, null);
    }
    /**
     * Updates a webhook rule that matches the specified id with the requested information.
     *
     * The PATCH method allows you to change specific values on the object while leaving other values alone.  As input you should supply a list of field names and new values.  If you do not provide the name of a field, that field will remain unchanged.  This allows you to ensure that you are only updating the specific fields desired.
     *
     * @param id The unique Lockstep Platform ID number of the Webhook Rule to update.
     * @param body A list of changes to apply to this Webhook Rule
     */
    updateWebhookRule(id, body) {
        const url = `/api/v1/WebhookRules/${id}`;
        return this.client.request("patch", url, null, body);
    }
    /**
     * Deletes the Webhook Rule referred to by this unique identifier.
     *
     * @param id The unique Lockstep Platform ID number of the Webhook Rule to delete.
     */
    deleteWebhookRule(id) {
        const url = `/api/v1/WebhookRules/${id}`;
        return this.client.request("delete", url, null, null);
    }
    /**
     * Creates one or more webhook rules from a given model.
     *
     * @param body The Webhook Rules to create
     */
    createWebhookRules(body) {
        const url = `/api/v1/WebhookRules`;
        return this.client.request("post", url, null, body);
    }
    /**
     * Queries Webhook Rules for this account using the specified filtering, sorting, and pagination rules requested.
     *
     * More information on querying can be found on the [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight) page on the Lockstep Developer website.
     *
     * @param filter The filter for this query. See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param include To fetch additional data on this object, specify the list of elements to retrieve. No collections are currently available but may be offered in the future
     * @param order The sort order for this query. See See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageSize The page size for results (default 250, maximum of 500). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageNumber The page number for results (default 0). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     */
    queryWebhookRules(filter, include, order, pageSize, pageNumber) {
        const url = `/api/v1/WebhookRules/query`;
        const options = {
            params: {
                filter,
                include,
                order,
                pageSize,
                pageNumber,
            },
        };
        return this.client.request("get", url, options, null);
    }
}
//# sourceMappingURL=WebhookRulesClient.js.map